import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowLeft, Download, Palette, Sparkles, Printer } from "lucide-react";
import { characters } from "@/data/characters";

export default function ColoringPage() {
  return (
    <div className="pt-32 md:pt-40 pb-24 min-h-screen relative overflow-hidden" data-testid="coloring-page">
      <div className="aurora" style={{ width: 480, height: 480, background: "#FDE68A", top: "5%", left: "-10%" }} />
      <div className="aurora" style={{ width: 420, height: 420, background: "#FECACA", bottom: "0%", right: "-10%" }} />

      <div className="max-w-6xl mx-auto px-6 md:px-10 relative">
        <Link to="/" data-testid="coloring-back" className="inline-flex items-center gap-2 chip !bg-white/80 !border-white">
          <ArrowLeft size={14} /> Strona główna
        </Link>

        <div className="mt-8 flex flex-col md:flex-row md:items-end md:justify-between gap-8">
          <div>
            <span className="chip"><Palette size={12} /> Kolorowanki</span>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9 }}
              className="font-display font-semibold text-[#051937] text-4xl sm:text-5xl md:text-6xl mt-6 tracking-tight leading-[1.02]"
            >
              Wydrukuj, pokoloruj,{" "}
              <em className="not-italic text-[#60A5FA]">powieś na lodówce.</em>
            </motion.h1>
            <p className="mt-6 text-lg text-[#0b2a55]/75 max-w-xl">
              Sześć darmowych kolorowanek z bohaterami serii. Wystarczy kartka A4, kredki i chwila dziecka bez ekranu.
            </p>
          </div>
          <div className="chip !bg-white !border-[#BAE6FD]">
            <Printer size={12} /> Format A4 · druk domowy
          </div>
        </div>

        {/* Grid */}
        <motion.ul
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          variants={{ hidden: {}, show: { transition: { staggerChildren: 0.08 } } }}
          className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8"
        >
          {characters.slice(0, 6).map((c, i) => (
            <motion.li
              key={c.slug}
              variants={{
                hidden: { opacity: 0, y: 40 },
                show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] } },
              }}
              whileHover={{ y: -6, rotate: i % 2 === 0 ? -0.6 : 0.6 }}
              className="group rounded-[2rem] bg-white border border-[#BAE6FD]/70 overflow-hidden shadow-[0_15px_40px_-20px_rgba(11,42,85,0.25)]"
              data-testid={`coloring-${c.slug}`}
            >
              <div
                className="aspect-[3/4] relative overflow-hidden"
                style={{ background: `${c.color}22` }}
              >
                <motion.img
                  src={c.portrait}
                  alt=""
                  className="absolute inset-0 w-full h-full object-contain p-6"
                  style={{ filter: "grayscale(100%) contrast(1.4) opacity(0.85)" }}
                  whileHover={{ scale: 1.05, rotate: [-1, 1, -1, 0] }}
                  transition={{ duration: 0.7 }}
                />
                <span className="absolute top-3 left-3 chip !bg-white !border-white !text-[#051937]">
                  <Sparkles size={12} /> darmowe
                </span>
              </div>
              <div className="p-5">
                <h3 className="font-display font-semibold text-[#051937] text-xl">
                  Kolorowanka: {c.name}
                </h3>
                <p className="mt-1 text-xs uppercase tracking-[0.14em] font-bold text-[#0b2a55]/60">
                  PDF · A4 · dla wieku 3+
                </p>
                <a
                  href={c.portrait}
                  download={`kolorowanka-${c.slug}.png`}
                  className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#051937] hover:bg-[#0b2a55] text-white px-4 py-2 text-xs font-semibold"
                  data-testid={`download-${c.slug}`}
                >
                  <Download size={14} /> Pobierz
                </a>
              </div>
            </motion.li>
          ))}
        </motion.ul>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9 }}
          className="mt-20 rounded-[2rem] md:rounded-[3rem] bg-[#84C5F4] p-8 md:p-14 text-center relative overflow-hidden"
        >
          <div className="relative z-10 max-w-xl mx-auto">
            <h2 className="font-display font-semibold text-white text-3xl md:text-4xl leading-tight">
              Chcesz cały pakiet 12 kolorowanek?
            </h2>
            <p className="mt-4 text-white/90">
              Pełna paczka PDF z 12 stronami do wydrukowania — 15 zł.
            </p>
            <Link
              to="/sklep"
              data-testid="coloring-shop-cta"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#051937] text-white px-7 py-4 font-semibold"
            >
              Do sklepu <Download size={16} />
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
